import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { BookOpen, ArrowDown, ArrowUp, Wallet, FunnelSimple, HourglassMedium, FilePdf, FileXls, Scales, DownloadSimple, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { withDatePrefix } from "@/lib/downloadFilename";
import PageShortcutHeader from "@/components/PageShortcutHeader";
import { sortPaymentModeStrings, formatPaymentModeName } from "@/lib/paymentModes";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const csvCell = (v) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n || 0);
const inr0 = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);
const today = () => new Date().toISOString().slice(0, 10);
const dmy = (s) => (s ? String(s).slice(0, 10).split("-").reverse().join("-") : "");

// v1.29.92 — Day Book column sorter. Received = money-in amount, Paid =
// money-out amount, net = the row's running net. Party sorts alphabetically.
// sort.by === null keeps the backend's default chronological order.
const sortRows = (arr, sort) => {
    if (!sort.by) return arr;
    const dir = sort.dir === "desc" ? -1 : 1;
    const val = (r) => {
        switch (sort.by) {
            case "party": return (r.party_name || "").toLowerCase();
            case "received": return r.direction === "in" ? (r.amount || 0) : 0;
            case "paid": return r.direction === "out" ? (r.amount || 0) : 0;
            case "net": return r.running_net || 0;
            default: return 0;
        }
    };
    return [...arr].sort((a, b) => {
        const va = val(a), vb = val(b);
        if (sort.by === "party") { // keep party-less (—) rows at the bottom either way
            const ea = va === "", eb = vb === "";
            if (ea && !eb) return 1;
            if (!ea && eb) return -1;
        }
        if (va < vb) return -1 * dir;
        if (va > vb) return 1 * dir;
        return 0;
    });
};

/**
 * v1.25.81 — Day Book report
 * Pure cash-in / cash-out for a chosen period. Excludes commission / portal
 * fees / invoices / adjustments so admins see only real money movement.
 */
export default function DayBook() {
    const [dateFrom, setDateFrom] = useState(today());
    const [dateTo, setDateTo] = useState(today());
    const [partyId, setPartyId] = useState("all");
    const [paymentMode, setPaymentMode] = useState("");
    const [createdBy, setCreatedBy] = useState("all");
    const [parties, setParties] = useState([]);
    const [users, setUsers] = useState([]);
    const [availableModes, setAvailableModes] = useState([]);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    // v1.29.92 — Client-side column sort (Party A–Z/Z–A, Received/Paid/Running
    // Net low–high/high–low). null = the backend's default chronological order.
    const [sort, setSort] = useState({ by: null, dir: "asc" });
    const toggleSort = (col) => setSort((s) => (s.by === col ? { by: col, dir: s.dir === "asc" ? "desc" : "asc" } : { by: col, dir: "asc" }));
    const [reconcileOpen, setReconcileOpen] = useState(false);
    const [markRow, setMarkRow] = useState(null); // { id, source, note }
    const [marking, setMarking] = useState(false);
    const [backdatedOnly, setBackdatedOnly] = useState(false);

    useEffect(() => {
        api.get("/parties").then((r) => setParties(r.data || [])).catch(() => setParties([]));
        // v1.28.30 — Fetch users so admins can filter Day Book rows by creator.
        api.get("/users")
            .then((r) => setUsers((r.data || []).filter((u) => u.email)))
            .catch(() => setUsers([]));
    }, []);

    // v1.28.93 — Load Payment Mode options for the CURRENT date range every
    // time Apply is clicked (and on first mount). Only modes with real
    // transactions in the range are returned; the previously-selected mode
    // is cleared automatically if it no longer exists in the new window,
    // so the UI never shows a stale value.
    const loadModes = async (df, dt, pid, cb) => {
        try {
            const params = { date_from: df, date_to: dt };
            if (pid && pid !== "all") params.party_id = pid;
            if (cb && cb !== "all") params.created_by = cb;
            const r = await api.get("/reports/day-book/payment-modes", { params });
            const modes = r.data?.modes || [];
            setAvailableModes(modes);
            // Clear the current mode if it's no longer valid for this range.
            if (paymentMode && !modes.some((m) => m.toLowerCase() === paymentMode.toLowerCase())) {
                setPaymentMode("");
            }
        } catch {
            setAvailableModes([]);
        }
    };

    const load = async () => {
        setLoading(true);
        try {
            // Refresh the mode list against the currently-applied range
            // BEFORE fetching rows — this guarantees the dropdown reflects
            // whatever the user just picked in date/party/added-by even
            // when the request for /reports/day-book itself fails.
            await loadModes(dateFrom, dateTo, partyId, createdBy);
            const params = { date_from: dateFrom, date_to: dateTo };
            if (partyId !== "all") params.party_id = partyId;
            if (paymentMode.trim()) params.payment_mode = paymentMode.trim();
            if (createdBy !== "all") params.created_by = createdBy;
            const r = await api.get("/reports/day-book", { params });
            setData(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load Day Book");
            setData(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const t = data?.totals || { received: 0, paid: 0, net: 0, receipt_count: 0, payment_count: 0 };
    const baseRows = data?.rows || [];
    const rows = sortRows(backdatedOnly ? baseRows.filter((r) => r.backdated) : baseRows, sort);
    const pending = data?.pending || { rows: [], count: 0, amount: 0 };
    const reconciledCount = baseRows.filter((r) => r.reconciled).length;
    const backdatedCount = baseRows.filter((r) => r.backdated).length;

    // Quick-tick a row reconciled/unreconciled (persists to DB, global status)
    const toggleTick = async (r) => {
        const next = !r.ticked;
        const match = (x) => x.id === r.id && x.source === r.source;
        const apply = (val) => setData((d) => d ? {
            ...d,
            rows: d.rows.map((x) => match(x) ? { ...x, ticked: val, reconciled: val || !!((x.reference || "").trim()) } : x),
        } : d);
        apply(next);
        try {
            await api.post(`/reports/day-book/${r.id}/reconcile`, { source: r.source, reconciled: next, note: r.reconcile_note || "" });
        } catch (e) {
            apply(!next);
            toast.error("Failed to update reconcile status");
        }
    };

    // Reconcile summary — computed client-side from the loaded rows
    const summary = useMemo(() => {
        const byMode = {};
        const byParty = {};
        const unreconciled = [];
        let paidOut = 0, received = 0;
        for (const r of baseRows) {
            if (r.direction === "out") paidOut += r.amount; else received += r.amount;
            const m = r.payment_mode ? formatPaymentModeName(r.payment_mode) : "(no mode)";
            byMode[m] = (byMode[m] || 0) + r.amount;
            const pn = r.party_name || "(unknown)";
            if (!byParty[pn]) byParty[pn] = { payout: 0, payment: 0 };
            byParty[pn][r.direction === "out" ? "payout" : "payment"] += r.amount;
            if (!r.reconciled) unreconciled.push(r);
        }
        return {
            paidOut, received, net: received - paidOut,
            byMode: Object.entries(byMode).sort((a, b) => b[1] - a[1]),
            byParty: Object.entries(byParty).sort((a, b) => (b[1].payout + b[1].payment) - (a[1].payout + a[1].payment)),
            unreconciled,
        };
    }, [baseRows]);

    const exportCsv = () => {
        const lines = [];
        lines.push(["Day Book — Reconciliation Summary"].map(csvCell).join(","));
        lines.push([`Period: ${data?.date_from || "—"} to ${data?.date_to || "—"}`].map(csvCell).join(","));
        lines.push("");
        lines.push(["Total Paid Out", summary.paidOut].map(csvCell).join(","));
        lines.push(["Total Received", summary.received].map(csvCell).join(","));
        lines.push(["Net Position", summary.net].map(csvCell).join(","));
        lines.push("");
        lines.push(["Breakdown by Mode", "Amount"].map(csvCell).join(","));
        summary.byMode.forEach(([m, amt]) => lines.push([m, amt].map(csvCell).join(",")));
        lines.push("");
        lines.push(["Breakdown by Party", "Paid Out", "Received"].map(csvCell).join(","));
        summary.byParty.forEach(([p, v]) => lines.push([p, v.payout, v.payment].map(csvCell).join(",")));
        lines.push("");
        lines.push([`Unreconciled entries (${summary.unreconciled.length})`].map(csvCell).join(","));
        lines.push(["When", "Party", "Type", "Mode", "Amount"].map(csvCell).join(","));
        summary.unreconciled.forEach((r) => lines.push([r.when, r.party_name, r.type_label, r.payment_mode, r.amount].map(csvCell).join(",")));
        const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = withDatePrefix(`day-book-reconcile-${data?.date_from || "all"}_to_${data?.date_to || "all"}.csv`);
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 4000);
    };

    const submitMark = async () => {
        if (!markRow) return;
        setMarking(true);
        try {
            await api.post(`/reports/day-book/${markRow.id}/reconcile`, { source: markRow.source, reconciled: true, note: markRow.note || "" });
            toast.success("Marked as reconciled");
            setMarkRow(null);
            await load();
        } catch (e) {
            toast.error("Failed to mark reconciled");
        } finally {
            setMarking(false);
        }
    };

    const ReconcileBadge = ({ r }) => (
        r.reconciled ? (
            <span
                data-testid={`daybook-badge-${r.id}`}
                title={r.reconcile_note ? `Reconciled · ${r.reconcile_note}` : "Reconciled"}
                className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 ring-1 ring-emerald-200"
            >
                <CheckCircle size={11} weight="fill" /> Reconciled
            </span>
        ) : (
            <button
                type="button"
                data-testid={`daybook-badge-${r.id}`}
                onClick={() => setMarkRow({ id: r.id, source: r.source, note: "" })}
                title="Click to mark as reconciled"
                className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700 ring-1 ring-amber-200 transition hover:bg-amber-100"
            >
                <WarningCircle size={11} weight="fill" /> Unreconciled
            </button>
        )
    );

    return (
        <div className="space-y-6" data-testid="day-book-page">
            <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-100 text-indigo-700">
                    <BookOpen size={22} weight="duotone" />
                </div>
                <PageShortcutHeader
                    action_id="page:day-book"
                    label="Day Book"
                    target_route="/admin/reports/day-book"
                    eyebrow="Reports"
                    title="Day Book"
                    titleClassName="font-display text-3xl font-bold tracking-tight text-slate-900"
                />
                <Button
                    data-testid="daybook-reconcile-btn"
                    variant="outline"
                    onClick={() => setReconcileOpen(true)}
                    disabled={!data}
                    className="ml-auto border-amber-200 text-amber-700 hover:bg-amber-50"
                >
                    <Scales size={14} className="mr-1.5" /> Reconcile
                </Button>
            </div>

            {/* Filters */}
            <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                    <FunnelSimple size={14} /> Filters
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-6">
                    <label className="flex flex-col text-xs text-slate-600">
                        From
                        <input type="date" data-testid="daybook-date-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1 rounded-md border border-slate-300 px-2 py-1.5 text-sm" />
                    </label>
                    <label className="flex flex-col text-xs text-slate-600">
                        To
                        <input type="date" data-testid="daybook-date-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1 rounded-md border border-slate-300 px-2 py-1.5 text-sm" />
                    </label>
                    <label className="flex flex-col text-xs text-slate-600">
                        Party
                        <select data-testid="daybook-party" value={partyId} onChange={(e) => setPartyId(e.target.value)} className="mt-1 rounded-md border border-slate-300 px-2 py-1.5 text-sm">
                            <option value="all">All Parties</option>
                            {parties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                    </label>
                    <label className="flex flex-col text-xs text-slate-600">
                        Payment Mode
                        <select
                            data-testid="daybook-mode"
                            value={paymentMode}
                            onChange={(e) => setPaymentMode(e.target.value)}
                            className="mt-1 rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                        >
                            <option value="">Any</option>
                            {/* v1.29.20 — Sort A-Z + Title Case via shared helper so the
                                filter dropdown matches the master Payment Modes list. */}
                            {sortPaymentModeStrings(availableModes).map((m) => (
                                <option key={m} value={m} data-testid={`daybook-mode-opt-${m}`}>{m}</option>
                            ))}
                        </select>
                    </label>
                    <label className="flex flex-col text-xs text-slate-600">
                        Added By
                        <select data-testid="daybook-created-by" value={createdBy} onChange={(e) => setCreatedBy(e.target.value)} className="mt-1 rounded-md border border-slate-300 px-2 py-1.5 text-sm">
                            <option value="all">All Users</option>
                            {users.map((u) => (
                                <option key={u.email} value={u.email}>{u.name || u.email}</option>
                            ))}
                        </select>
                    </label>
                    <div className="flex items-end gap-2">
                        <button
                            type="button"
                            data-testid="daybook-apply"
                            onClick={load}
                            disabled={loading}
                            className="flex-1 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-60"
                        >
                            {loading ? "Loading…" : "Apply"}
                        </button>
                        <DayBookDownload dateFrom={dateFrom} dateTo={dateTo} partyId={partyId} paymentMode={paymentMode} createdBy={createdBy} />
                    </div>
                </div>
            </div>

            {/* KPI cards */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
                <Card icon={ArrowDown} label="Total Received" value={inr0(t.received)} tone="emerald" testId="daybook-total-received" />
                <Card icon={ArrowUp} label="Total Paid" value={inr0(t.paid)} tone="rose" testId="daybook-total-paid" />
                <Card icon={Wallet} label="Net Amount" value={inr0(t.net)} tone={t.net >= 0 ? "emerald" : "rose"} testId="daybook-net" />
                <Card label="Receipt Entries" value={t.receipt_count} tone="slate" testId="daybook-count-in" />
                <Card label="Payment Entries" value={t.payment_count} tone="slate" testId="daybook-count-out" />
            </div>

            {/* Rows */}
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-slate-100 px-5 py-3">
                    <h2 className="font-display text-base font-semibold text-slate-900">
                        Transactions {rows.length > 0 ? `· ${rows.length}` : ""}
                    </h2>
                    {baseRows.length > 0 && (
                        <span data-testid="daybook-recon-summary" className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200">
                            <CheckCircle size={12} weight="fill" /> {reconciledCount} of {baseRows.length} entries reconciled
                        </span>
                    )}
                    {baseRows.length > 0 && (
                        <span data-testid="daybook-backdated-summary" className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800 ring-1 ring-amber-300">
                            {backdatedCount} backdated
                        </span>
                    )}
                    <label className="ml-auto inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-amber-300 bg-white px-2.5 py-0.5 text-[11px] font-semibold text-amber-800 hover:bg-amber-50" data-testid="daybook-backdated-filter-label">
                        <input type="checkbox" data-testid="daybook-backdated-filter" checked={backdatedOnly} onChange={(e) => setBackdatedOnly(e.target.checked)} className="h-3.5 w-3.5 cursor-pointer accent-amber-600" />
                        <span className="uppercase tracking-wider">Show backdated only</span>
                    </label>
                </div>
                <div className="hidden max-h-[70vh] overflow-auto rounded-lg border border-slate-100 md:block">
                    <table className="w-full text-sm" data-testid="daybook-table">
                        <thead className="sticky top-0 z-10 bg-slate-100 text-xs uppercase tracking-wider text-slate-600 shadow-sm">
                            <tr>
                                <th className="px-2 py-2 text-center w-10" title="Reconciled">✓</th>
                                <th className="px-3 py-2 text-left">Date / Time</th>
                                <th className="px-3 py-2 text-left">
                                    <button type="button" data-testid="daybook-sort-party" onClick={() => toggleSort("party")} className="inline-flex items-center gap-1 uppercase tracking-wider transition-colors hover:text-slate-800">
                                        Party
                                        <span className="text-[10px]">{sort.by === "party" ? (sort.dir === "asc" ? "▲" : "▼") : "↕"}</span>
                                    </button>
                                </th>
                                <th className="px-3 py-2 text-left">Type</th>
                                <th className="px-3 py-2 text-left">Mode</th>
                                <th className="px-3 py-2 text-left">UTR / Ref</th>
                                <th className="px-3 py-2 text-left">Added By</th>
                                <th className="px-3 py-2 text-right">
                                    <button type="button" data-testid="daybook-sort-received" onClick={() => toggleSort("received")} className="inline-flex w-full items-center justify-end gap-1 uppercase tracking-wider transition-colors hover:text-slate-800">
                                        Received
                                        <span className="text-[10px]">{sort.by === "received" ? (sort.dir === "asc" ? "▲" : "▼") : "↕"}</span>
                                    </button>
                                </th>
                                <th className="px-3 py-2 text-right">
                                    <button type="button" data-testid="daybook-sort-paid" onClick={() => toggleSort("paid")} className="inline-flex w-full items-center justify-end gap-1 uppercase tracking-wider transition-colors hover:text-slate-800">
                                        Paid
                                        <span className="text-[10px]">{sort.by === "paid" ? (sort.dir === "asc" ? "▲" : "▼") : "↕"}</span>
                                    </button>
                                </th>
                                <th className="px-3 py-2 text-right">
                                    <button type="button" data-testid="daybook-sort-net" onClick={() => toggleSort("net")} className="inline-flex w-full items-center justify-end gap-1 uppercase tracking-wider transition-colors hover:text-slate-800">
                                        Running Net
                                        <span className="text-[10px]">{sort.by === "net" ? (sort.dir === "asc" ? "▲" : "▼") : "↕"}</span>
                                    </button>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 && (
                                <tr><td colSpan={10} className="px-3 py-10 text-center text-slate-400">No transactions in this range.</td></tr>
                            )}
                            {rows.map((r) => {
                                const ticked = !!r.ticked;
                                return (
                                <tr key={`${r.source}-${r.id}`} data-testid={`daybook-row-${r.id}`} data-reconciled={r.reconciled ? "1" : "0"} data-backdated={r.backdated ? "1" : "0"} data-suspense={r.is_suspense ? "1" : "0"} className={`border-t border-slate-100 ${r.is_suspense ? "bg-orange-100 hover:bg-orange-200" : ticked ? "bg-emerald-700" : r.backdated ? "bg-amber-50 hover:bg-amber-100" : "hover:bg-slate-50"}`}>
                                    <td className="px-2 py-2 text-center">
                                        <input
                                            type="checkbox"
                                            data-testid={`daybook-tick-${r.id}`}
                                            checked={ticked}
                                            onChange={() => toggleTick(r)}
                                            title={ticked ? "Reconciled — click to unreconcile" : "Tick to reconcile"}
                                            className="h-4 w-4 cursor-pointer accent-emerald-600"
                                        />
                                    </td>
                                    <td className={`px-3 py-2 text-xs whitespace-nowrap ${ticked ? "text-emerald-50" : "text-slate-600"}`}>
                                        <div>{r.when.replace("T", " · ").slice(0, 19)}</div>
                                        {r.backdated && (
                                            <div className={`mt-0.5 flex flex-wrap items-center gap-1 ${ticked ? "text-amber-100" : "text-amber-700"}`} title={`Transaction date ${dmy(r.txn_date)} · ${r.source === "slip" ? "Uploaded" : "Entered"} ${dmy(r.entry_date)}`} data-testid={`daybook-backdated-${r.id}`}>
                                                <span className="rounded bg-amber-400 px-1 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-950">Backdated</span>
                                                <span className="text-[10px] font-semibold">{r.source === "slip" ? "Uploaded" : "Entered"}: {dmy(r.entry_date)}</span>
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-3 py-2">
                                        {r.party_id ? (
                                            <Link to={`/admin/reports?party=${r.party_id}`} className={`font-medium hover:underline ${ticked ? "text-white underline" : "text-indigo-700"}`} data-testid={`daybook-party-link-${r.id}`}>
                                                {r.party_name || "—"}
                                            </Link>
                                        ) : (ticked ? <span className="text-emerald-50">—</span> : "—")}
                                    </td>
                                    <td className="px-3 py-2 text-xs">
                                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${r.direction === "in" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>
                                            {r.type_label}
                                        </span>
                                    </td>
                                    <td className={`px-3 py-2 text-xs ${ticked ? "text-emerald-50" : "text-slate-700"}`}>
                                        {r.payment_mode ? formatPaymentModeName(r.payment_mode) : "—"}
                                        {r.is_suspense && (
                                            <span data-testid={`daybook-suspense-${r.id}`} className="ml-1.5 inline-flex items-center gap-1 rounded-full bg-orange-500 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">SUSPENSE</span>
                                        )}
                                    </td>
                                    <td className={`px-3 py-2 font-mono text-xs ${ticked ? "text-emerald-50" : "text-slate-700"}`}>
                                        <div className="flex flex-col items-start gap-1">
                                            <span>
                                                {r.upload_id ? (
                                                    <Link to={`/admin/uploads?open=${r.upload_id}`} className={`hover:underline ${ticked ? "text-white underline" : "text-indigo-700"}`} data-testid={`daybook-slip-link-${r.id}`}>
                                                        {r.reference || "view slip"}
                                                    </Link>
                                                ) : (r.reference || "—")}
                                            </span>
                                            <ReconcileBadge r={r} />
                                        </div>
                                    </td>
                                    <td className={`px-3 py-2 text-xs ${ticked ? "text-emerald-50" : "text-slate-700"}`} data-testid={`daybook-added-by-${r.id}`}>{r.added_by || "System"}</td>
                                    <td className={`px-3 py-2 text-right tabular-nums ${ticked ? "text-white" : "text-emerald-800"}`}>{r.direction === "in" ? inr(r.amount) : ""}</td>
                                    <td className={`px-3 py-2 text-right tabular-nums ${ticked ? "text-white" : "text-rose-800"}`}>{r.direction === "out" ? inr(r.amount) : ""}</td>
                                    <td className={`px-3 py-2 text-right tabular-nums font-semibold ${ticked ? "text-white" : (r.running_net >= 0 ? "text-emerald-800" : "text-rose-800")}`}>{inr(r.running_net)}</td>
                                </tr>
                                );
                            })}
                        </tbody>
                        {rows.length > 0 && (
                            <tfoot className="border-t-2 border-slate-300 bg-slate-50 font-semibold">
                                <tr>
                                    <td className="px-3 py-2 text-xs uppercase tracking-wider text-slate-500" colSpan={7}>Total</td>
                                    <td className="px-3 py-2 text-right tabular-nums text-emerald-800" data-testid="daybook-foot-received">{inr(t.received)}</td>
                                    <td className="px-3 py-2 text-right tabular-nums text-rose-800" data-testid="daybook-foot-paid">{inr(t.paid)}</td>
                                    <td className={`px-3 py-2 text-right tabular-nums ${t.net >= 0 ? "text-emerald-800" : "text-rose-800"}`} data-testid="daybook-foot-net">{inr(t.net)}</td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
                {/* v1.29.96 — Mobile card list for the day-book (no horizontal scroll). */}
                <div className="md:hidden">
                    <div className="flex flex-wrap gap-1.5 px-2 pb-2">
                        {[["party", "Party"], ["received", "Recd"], ["paid", "Paid"], ["net", "Net"]].map(([col, lbl]) => (
                            <button
                                key={col}
                                type="button"
                                data-testid={`daybook-sortm-${col}`}
                                onClick={() => toggleSort(col)}
                                className={`rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors ${sort.by === col ? "bg-slate-800 text-white" : "border border-slate-200 bg-white text-slate-600"}`}
                            >
                                {lbl}{sort.by === col ? (sort.dir === "asc" ? " ▲" : " ▼") : ""}
                            </button>
                        ))}
                    </div>
                    <div className="space-y-2 px-2">
                        {rows.length === 0 && (
                            <div className="py-8 text-center text-sm text-slate-400">No transactions in this range.</div>
                        )}
                        {rows.map((r) => {
                            const ticked = !!r.ticked;
                            return (
                            <div key={`${r.source}-${r.id}`} data-testid={`daybook-card-${r.id}`} data-reconciled={r.reconciled ? "1" : "0"} data-backdated={r.backdated ? "1" : "0"} data-suspense={r.is_suspense ? "1" : "0"} className={`rounded-lg border p-3 ${r.is_suspense ? "border-orange-400 bg-orange-100" : ticked ? "border-emerald-800 bg-emerald-700 text-white" : r.backdated ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-white"}`}>
                                <div className="flex items-start justify-between gap-2">
                                    <div className="flex min-w-0 items-start gap-2">
                                        <input
                                            type="checkbox"
                                            data-testid={`daybook-tickm-${r.id}`}
                                            checked={ticked}
                                            onChange={() => toggleTick(r)}
                                            className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-emerald-600"
                                        />
                                        <div className="min-w-0">
                                            <div className="truncate font-medium">
                                                {r.party_id ? (
                                                    <Link to={`/admin/reports?party=${r.party_id}`} className={`hover:underline ${ticked ? "text-white underline" : "text-indigo-700"}`}>{r.party_name || "—"}</Link>
                                                ) : "—"}
                                            </div>
                                            <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[10px]">
                                                <span className={`rounded-full px-2 py-0.5 font-semibold ${r.direction === "in" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>{r.type_label}</span>
                                                <span className={ticked ? "text-emerald-50" : "text-slate-500"}>{r.payment_mode ? formatPaymentModeName(r.payment_mode) : "—"}</span>
                                                {r.is_suspense && <span data-testid={`daybook-suspense-m-${r.id}`} className="inline-flex items-center rounded-full bg-orange-500 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">SUSPENSE</span>}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="shrink-0 text-right">
                                        <div className={`font-bold tabular-nums ${ticked ? "text-white" : (r.direction === "in" ? "text-emerald-800" : "text-rose-800")}`}>{r.direction === "in" ? "+" : "−"}{inr(r.amount)}</div>
                                        <div className={`mt-0.5 text-[11px] tabular-nums ${ticked ? "text-emerald-50" : (r.running_net >= 0 ? "text-emerald-700" : "text-rose-700")}`}>Net {inr(r.running_net)}</div>
                                    </div>
                                </div>
                                <div className={`mt-1.5 flex flex-wrap items-center justify-between gap-1.5 text-[10px] ${ticked ? "text-emerald-50" : "text-slate-500"}`}>
                                    <span className="font-mono">
                                        {r.upload_id ? (
                                            <Link to={`/admin/uploads?open=${r.upload_id}`} className={`hover:underline ${ticked ? "text-white underline" : "text-indigo-700"}`}>{r.reference || "view slip"}</Link>
                                        ) : (r.reference || "—")}
                                    </span>
                                    <ReconcileBadge r={r} />
                                </div>
                                <div className={`mt-1 text-right text-[10px] ${ticked ? "text-emerald-50" : "text-slate-500"}`}>
                                    {r.backdated && (
                                        <span className={`mr-1 inline-flex items-center gap-1 rounded bg-amber-400 px-1 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-950`} data-testid={`daybook-backdated-m-${r.id}`} title={`Transaction date ${dmy(r.txn_date)} · ${r.source === "slip" ? "Uploaded" : "Entered"} ${dmy(r.entry_date)}`}>
                                            Backdated · {r.source === "slip" ? "Up" : "In"} {dmy(r.entry_date)}
                                        </span>
                                    )}
                                    {r.when.replace("T", " · ").slice(0, 16)} · {r.added_by || "System"}
                                </div>
                            </div>
                            );
                        })}
                        {rows.length > 0 && (
                            <div className="flex items-center justify-between rounded-lg border-2 border-slate-300 bg-slate-50 px-3 py-2 text-sm font-bold" data-testid="daybook-cards-total">
                                <span className="text-slate-600">Total</span>
                                <span className="tabular-nums">
                                    <span className="text-emerald-800">{inr(t.received)}</span> / <span className="text-rose-800">{inr(t.paid)}</span> / <span className={t.net >= 0 ? "text-emerald-800" : "text-rose-800"}>{inr(t.net)}</span>
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Pending slips section */}
            <div className="rounded-lg border border-amber-200 bg-amber-50/40 shadow-sm" data-testid="daybook-pending-section">
                <div className="flex items-center justify-between border-b border-amber-200 px-5 py-3">
                    <div className="flex items-center gap-2 font-display text-base font-semibold text-amber-900">
                        <HourglassMedium size={16} weight="duotone" />
                        Pending Slips
                        <span className="ml-1 text-xs font-normal text-amber-700">(not included in Total Received)</span>
                    </div>
                    <div className="text-xs text-amber-800" data-testid="daybook-pending-summary">
                        {pending.count} slip{pending.count === 1 ? "" : "s"} · {inr(pending.amount)}
                    </div>
                </div>
                <div className="hidden overflow-x-auto md:block">
                    <table className="w-full text-sm">
                        <thead className="bg-amber-100/50 text-xs uppercase tracking-wider text-amber-800">
                            <tr>
                                <th className="px-3 py-2 text-left">Date</th>
                                <th className="px-3 py-2 text-left">Party</th>
                                <th className="px-3 py-2 text-left">UTR</th>
                                <th className="px-3 py-2 text-left">Mode</th>
                                <th className="px-3 py-2 text-right">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pending.rows.length === 0 && (
                                <tr><td colSpan={5} className="px-3 py-6 text-center text-amber-700">No pending slips.</td></tr>
                            )}
                            {pending.rows.map((p) => (
                                <tr key={p.id} className="border-t border-amber-100" data-testid={`daybook-pending-${p.id}`}>
                                    <td className="px-3 py-2 text-xs text-slate-600">{p.payment_date || "—"}</td>
                                    <td className="px-3 py-2 text-slate-800">{p.party_name || "—"}</td>
                                    <td className="px-3 py-2 font-mono text-xs">
                                        <Link to={`/admin/uploads?open=${p.id}`} className="text-indigo-700 hover:underline">{p.utr || "view slip"}</Link>
                                    </td>
                                    <td className="px-3 py-2 text-xs">{p.payment_app || "—"}</td>
                                    <td className="px-3 py-2 text-right tabular-nums font-semibold text-amber-900">{inr(p.amount)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {/* v1.29.96 — Mobile card list for pending slips (no horizontal scroll). */}
                <div className="space-y-2 p-2 md:hidden" data-testid="daybook-pending-cards">
                    {pending.rows.length === 0 && (
                        <div className="py-6 text-center text-sm text-amber-700">No pending slips.</div>
                    )}
                    {pending.rows.map((p) => (
                        <div key={p.id} data-testid={`daybook-pending-card-${p.id}`} className="rounded-lg border border-amber-200 bg-white p-3">
                            <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                    <div className="truncate font-medium text-slate-900">{p.party_name || "—"}</div>
                                    <div className="mt-0.5 font-mono text-xs">
                                        <Link to={`/admin/uploads?open=${p.id}`} className="text-indigo-700 hover:underline">{p.utr || "view slip"}</Link>
                                    </div>
                                </div>
                                <div className="shrink-0 text-right">
                                    <div className="font-bold tabular-nums text-amber-900">{inr(p.amount)}</div>
                                    <div className="mt-0.5 text-[10px] text-slate-500">{p.payment_date || "—"} · {p.payment_app || "—"}</div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Reconcile summary dialog */}
            <Dialog open={reconcileOpen} onOpenChange={setReconcileOpen}>
                <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="daybook-reconcile-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Scales size={18} className="text-amber-600" /> Reconciliation Summary
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-5">
                        <div className="text-xs text-slate-500">
                            Period: <span className="font-semibold text-slate-700">{data?.date_from || "—"}</span> to <span className="font-semibold text-slate-700">{data?.date_to || "—"}</span>
                        </div>

                        <div className="grid grid-cols-3 gap-3">
                            <div className="rounded-lg border border-rose-200 bg-rose-50/60 p-3">
                                <div className="text-[10px] font-semibold uppercase tracking-wider text-rose-800">Total Paid Out</div>
                                <div data-testid="daybook-rec-paidout" className="mt-1 text-lg font-bold text-rose-900 tabular-nums">{inr0(summary.paidOut)}</div>
                            </div>
                            <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3">
                                <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-800">Total Received</div>
                                <div data-testid="daybook-rec-received" className="mt-1 text-lg font-bold text-emerald-900 tabular-nums">{inr0(summary.received)}</div>
                            </div>
                            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-600">Net Position</div>
                                <div data-testid="daybook-rec-net" className={`mt-1 text-lg font-bold tabular-nums ${summary.net < 0 ? "text-rose-700" : "text-slate-900"}`}>{inr0(summary.net)}</div>
                            </div>
                        </div>

                        <div>
                            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500">Breakdown by Mode</div>
                            <div className="rounded-lg border border-slate-200">
                                {summary.byMode.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">No data</div>}
                                {summary.byMode.map(([m, amt]) => (
                                    <div key={m} className="flex items-center justify-between border-b border-slate-100 px-3 py-1.5 text-sm last:border-0">
                                        <span className="text-slate-700">{m}</span>
                                        <span className="font-semibold tabular-nums text-slate-900">{inr0(amt)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div>
                            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500">Breakdown by Party</div>
                            <div className="max-h-40 overflow-y-auto rounded-lg border border-slate-200">
                                {summary.byParty.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">No data</div>}
                                {summary.byParty.map(([p, v]) => (
                                    <div key={p} className="flex items-center justify-between border-b border-slate-100 px-3 py-1.5 text-sm last:border-0">
                                        <span className="text-slate-700">{p}</span>
                                        <span className="flex gap-3 text-xs tabular-nums">
                                            {v.payout > 0 && <span className="text-rose-700">Out {inr0(v.payout)}</span>}
                                            {v.payment > 0 && <span className="text-emerald-700">In {inr0(v.payment)}</span>}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div>
                            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500">
                                Unreconciled Entries ({summary.unreconciled.length}) — missing UTR / Reference
                            </div>
                            <div className="max-h-40 overflow-y-auto rounded-lg border border-amber-200 bg-amber-50/40">
                                {summary.unreconciled.length === 0 && <div className="px-3 py-2 text-xs text-emerald-600">All entries reconciled ✓</div>}
                                {summary.unreconciled.map((r) => (
                                    <div key={`${r.source}-${r.id}`} className="flex items-center justify-between border-b border-amber-100 px-3 py-1.5 text-xs last:border-0">
                                        <span className="text-slate-700">{r.when.replace("T", " · ").slice(0, 16)} · {r.party_name || "—"} · {r.payment_mode ? formatPaymentModeName(r.payment_mode) : "no mode"}</span>
                                        <span className="font-semibold tabular-nums text-slate-900">{inr0(r.amount)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button data-testid="daybook-rec-export" variant="outline" onClick={exportCsv} className="border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                            <DownloadSimple size={14} className="mr-1.5" /> Export CSV
                        </Button>
                        <Button onClick={() => setReconcileOpen(false)} className="bg-slate-800 text-white hover:bg-slate-900">Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Mark reconciled dialog */}
            <Dialog open={!!markRow} onOpenChange={(o) => { if (!o) setMarkRow(null); }}>
                <DialogContent className="max-w-md" data-testid="daybook-mark-dialog">
                    <DialogHeader>
                        <DialogTitle>Mark as Reconciled</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-2">
                        <label className="text-xs uppercase tracking-wider text-slate-500">Note (optional)</label>
                        <Textarea
                            data-testid="daybook-mark-note"
                            value={markRow?.note || ""}
                            onChange={(e) => setMarkRow((s) => s ? { ...s, note: e.target.value } : s)}
                            placeholder="e.g. Verified against bank statement / confirmed on WhatsApp"
                            rows={3}
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setMarkRow(null)} disabled={marking}>Cancel</Button>
                        <Button data-testid="daybook-mark-submit" onClick={submitMark} disabled={marking} className="bg-emerald-700 text-white hover:bg-emerald-800">
                            {marking ? "Saving…" : "Mark Reconciled"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function DayBookDownload({ dateFrom, dateTo, partyId, paymentMode, createdBy }) {
    const [busy, setBusy] = useState(null);
    const download = async (kind) => {
        try {
            setBusy(kind);
            const params = { date_from: dateFrom, date_to: dateTo };
            if (partyId !== "all") params.party_id = partyId;
            if (paymentMode.trim()) params.payment_mode = paymentMode.trim();
            if (createdBy && createdBy !== "all") params.created_by = createdBy;
            const url = kind === "pdf" ? "/export/day-book.pdf" : "/export/day-book.xlsx";
            const res = await api.get(url, { params, responseType: "blob" });
            const blob = new Blob([res.data], {
                type: kind === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const objUrl = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = objUrl;
            a.download = withDatePrefix(`day-book-${dateFrom}_to_${dateTo}.${kind === "pdf" ? "pdf" : "xlsx"}`);
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(objUrl), 4000);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Download failed");
        } finally { setBusy(null); }
    };
    return (
        <div className="flex gap-1.5" data-testid="daybook-download">
            <button type="button" disabled={!!busy} onClick={() => download("pdf")} data-testid="daybook-download-pdf"
                className="inline-flex items-center gap-1 rounded-md border border-indigo-300 bg-white px-2.5 py-2 text-xs font-semibold text-indigo-700 hover:bg-indigo-50 disabled:opacity-60">
                <FilePdf size={14} weight="bold" />{busy === "pdf" ? "…" : "PDF"}
            </button>
            <button type="button" disabled={!!busy} onClick={() => download("xlsx")} data-testid="daybook-download-xlsx"
                className="inline-flex items-center gap-1 rounded-md border border-emerald-300 bg-white px-2.5 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-60">
                <FileXls size={14} weight="bold" />{busy === "xlsx" ? "…" : "Excel"}
            </button>
        </div>
    );
}

function Card({ icon: Icon, label, value, tone, testId }) {
    const toneCls = {
        emerald: "border-emerald-200 bg-emerald-50/60 text-emerald-900",
        rose: "border-rose-200 bg-rose-50/60 text-rose-900",
        slate: "border-slate-200 bg-white text-slate-800",
    }[tone] || "border-slate-200 bg-white text-slate-800";
    return (
        <div data-testid={testId} className={`rounded-lg border px-4 py-3 shadow-sm ${toneCls}`}>
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider opacity-70">
                {Icon && <Icon size={12} weight="bold" />} {label}
            </div>
            <div className="mt-1 font-display text-xl font-bold tabular-nums">{value}</div>
        </div>
    );
}
